import React from 'react';
import PropTypes from 'prop-types';
import Preloader from '@ajat/design-system/Preloader';
import { ComponentPage, DemoCard, Demo } from '../shared';

export const meta = {
  component: Preloader,
  componentName: 'Preloader',
  title: 'Preloader',
  group: 'Feedback',
  importPath: '@ajat/design-system/Preloader',
  description:
    'Indeterminate loading line — a dark segment sliding over a faint track. It replaces "Loading…" text on the Product, SchoolShop and MenuItemShop pages, so there is deliberately nothing to read: the only text it carries is an accessible name.',
  props: [
    ['label', 'string', "'Loading'", 'Accessible name only — sets aria-label on the role="status" root. It is NOT rendered visually.'],
    ['variant', "'default' | 'logo'", "'default'", 'logo — brand logotype stacked above the line'],
    ['brand', "'CLS' | 'ABC' | 'Overall' | 'Crownstudent'", "'CLS'", 'Brand logotype shown by variant="logo" (unknown → CLS)'],
    ['logo', 'string', '—', 'Logotype URL (e.g. from a CMS) shown above the line whatever the brand — implies the logo variant'],
    ['className', 'string', "''", 'Additional CSS class'],
  ],
  usage: [
    {
      title: 'A loading section',
      code: `import Preloader from '@ajat/design-system/Preloader';

// Nothing is drawn but the line; label is what a screen
// reader announces, so localize it per brand.
if (loading) return <Preloader label="Laddar produkter…" />;`,
    },
    {
      title: 'With the brand logotype',
      code: `// Logotype stacked above the line (shared with the DS Header via assets/logos)
if (loading) return <Preloader variant="logo" brand="ABC" label="Laddar…" />;`,
    },
    {
      title: 'When you want visible text too',
      code: `// The component renders no text. Put your own beside it.
<div className="flex flex-col items-center gap-[var(--space-ui-medium-s)]">
  <Preloader label="Indlæser produkter…" />
  <p className="typo-body-sm">Indlæser produkter…</p>
</div>`,
    },
  ],
};

export default function PreloaderPage({ brand = 'CLS' }) {
  return (
    <ComponentPage meta={meta}>
      <DemoCard title="Default">
        <Preloader />
      </DemoCard>

      <DemoCard title="With brand logo" hint={`variant="logo" — ${brand}`}>
        <Preloader variant="logo" brand={brand} />
      </DemoCard>

      <DemoCard title="In context" hint="a page loading state">
        <div className="min-h-[240px] flex items-center justify-center">
          <Preloader label="Loading products…" />
        </div>
      </DemoCard>

      <DemoCard
        title="The label is invisible by design"
        hint="all three are identical on screen"
      >
        <p
          className="typo-body-sm"
          style={{ color: 'var(--color-gray-7)', maxWidth: '68ch', paddingBottom: 'var(--space-ui-medium-m)' }}
        >
          <code style={{ color: 'var(--color-gray-10)' }}>label</code> only sets{' '}
          <code style={{ color: 'var(--color-gray-10)' }}>aria-label</code> on the{' '}
          <code style={{ color: 'var(--color-gray-10)' }}>role=&quot;status&quot;</code> root —
          it draws no text. The captions below are written by this page, not by the component; inspect each
          line to see its accessible name differ.
        </p>
        <div className="flex flex-col gap-[var(--space-ui-large-s)]">
          <Demo caption='aria-label="Indlæser produkter…"'>
            <Preloader label="Indlæser produkter…" />
          </Demo>
          <Demo caption='aria-label="Laddar produkter…"'>
            <Preloader label="Laddar produkter…" />
          </Demo>
          <Demo caption='aria-label="Ladataan tuotteita…"'>
            <Preloader label="Ladataan tuotteita…" />
          </Demo>
        </div>
      </DemoCard>

      <DemoCard title="With your own visible text" hint="pair it yourself when the page needs a message">
        <div className="min-h-[200px] flex flex-col items-center justify-center gap-[var(--space-ui-medium-s)]">
          <Preloader label="Indlæser produkter…" />
          <p className="typo-body-sm" style={{ color: 'var(--color-gray-6)' }}>Indlæser produkter…</p>
        </div>
      </DemoCard>

      <DemoCard title="Motion" surface={false}>
        <p className="typo-body-sm text-[var(--color-gray-7)] max-w-[68ch]">
          Loops on a 1.75s <code className="text-[var(--color-gray-10)]">ds-preloader-slide</code> keyframe
          using the DS <code className="text-[var(--color-gray-10)]">--transition-function-ui</code> easing.
          Under <code className="text-[var(--color-gray-10)]">prefers-reduced-motion</code> the animation
          slows to 3.25s rather than stopping, so the page still reads as busy.
        </p>
      </DemoCard>
    </ComponentPage>
  );
}

PreloaderPage.propTypes = {
  brand: PropTypes.oneOf(['CLS', 'ABC', 'Overall', 'Crownstudent']),
};
